import React from 'react';

import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import ModelTypeConstants, {SchemaType} from 'constants/ModelTypeConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface SchemaAndSchemaTypeSelectorProps {
  tagSchemaId: string | null;
  schemaType: SchemaType;
  onChange: (tagSchema: TagSchemaSummaryWithFieldsGraphQL | null, schemaType: SchemaType) => void;
  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}

/**
 * Dropdown for picking a batch or entity schema
 */
class SchemaAndSchemaTypeSelector extends React.Component<SchemaAndSchemaTypeSelectorProps> {
  dropdown: SelectDropdown | null = null;

  _renderSelectedSchemaAndType(): string {
    let tagSchemaName;
    if (this.props.tagSchemaId == null) {
      tagSchemaName = 'Any';
    } else {
      const tagSchema = this.props.tagSchemasById[this.props.tagSchemaId];
      tagSchemaName = tagSchema.name;
    }

    let schemaTypeName;
    if (this.props.schemaType === (ModelTypeConstants.TAG_SCHEMA as string)) {
      schemaTypeName = 'Entity';
    } else if (this.props.schemaType === (ModelTypeConstants.BATCH_SCHEMA as string)) {
      schemaTypeName = 'Batch';
    } else {
      throw new Error(`Unexpected schema type ${this.props.schemaType}`);
    }

    return RegistryUtil.formatSchemaNameAndType(tagSchemaName, schemaTypeName);
  }

  _handleSelectSchema = (schemaId: string): void => {
    const {tagSchema, schemaType} = (() => {
      if (schemaId === FileBrowserConstants.SCHEMA_ID_TAG_SCHEMA_ALL) {
        return {tagSchema: null, schemaType: ModelTypeConstants.TAG_SCHEMA};
      } else if (schemaId === FileBrowserConstants.SCHEMA_ID_BATCH_SCHEMA_ALL) {
        return {tagSchema: null, schemaType: ModelTypeConstants.BATCH_SCHEMA};
      }

      for (const schema of Object.values(this.props.tagSchemasById)) {
        if (schemaId === schema.id) {
          return {tagSchema: schema, schemaType: ModelTypeConstants.TAG_SCHEMA};
        }
      }

      throw new Error(`Unexpected schema ID ${schemaId}`);
    })();

    this.props.onChange(tagSchema, schemaType as unknown as SchemaType);
    if (this.dropdown) {
      closeSelectDropdown(this.dropdown);
    }
  };

  _getMenuOptionDatas(): Array<OptionData> {
    const tagSchemas = Object.values(this.props.tagSchemasById);

    return [
      {
        name: 'Entity',
        value: FileBrowserConstants.SCHEMA_ID_TAG_SCHEMA_ALL,
        submenu: [
          [
            {
              name: 'Any',
              value: FileBrowserConstants.SCHEMA_ID_TAG_SCHEMA_ALL,
            },
          ],

          tagSchemas.map((tagSchema) => ({
            name: tagSchema.name,
            value: tagSchema.id,
          })),
        ],
      },
    ];
  }

  render(): JSX.Element {
    return (
      <SelectDropdown
        displayName={this._renderSelectedSchemaAndType()}
        menu={<SearchableMenu onSelect={this._handleSelectSchema} optionDatas={this._getMenuOptionDatas()} />}
        ref={(node) => {
          this.dropdown = node;
        }}
      />
    );
  }
}

export default SchemaAndSchemaTypeSelector;
